import {getLogger} from '@/shared/utils'
import type {ShareRoot} from '../../core/storage'
import {resolveDocumentImage} from './document-references'
import {ExternalViewerConfig, ViewerDataSource} from './handler-structures'
import {presignOwnKey, presignSharedKey, splitObjectUri} from './storage-viewer-source'

/**
 * Signs the images a storage-backed document (a Markdown file in html-viewer) references by storage
 * path, so they render next to its text.
 *
 * The viewer holds a presigned URL for the document alone and cannot reach its neighbours; the host
 * knows where the document lives and whose space it is in. Each image is signed exactly as the
 * document was: the user's own files with their own credentials, a shared folder's through the
 * sharing API and only inside that folder. The URLs live in the rendered page only — they are never
 * written back into the file, where they would expire.
 *
 * Wire contract (html-viewer `HtmlViewer.ts`):
 *   viewer → host  {action: 'resolveDocumentResources', requestId, refs: string[]}
 *   host → viewer  {action: 'resolveDocumentResources', requestId, urls: {[ref]: url}}
 * A ref the host will not sign is absent from `urls`, and the host always replies, so the viewer
 * never waits on its timeout.
 */
export const RESOLVE_DOCUMENT_RESOURCES = 'resolveDocumentResources'

/** More images than any report holds; bounds the work a crafted document can ask for. */
export const MAX_DOCUMENT_RESOURCES = 100

/**
 * `objectUri` with its key replaced by `key`: same bucket, same space. Built from the URI's own
 * leading segments, so the signing helpers apply their usual checks to it — whose space it is in,
 * and for a share whether it is inside the shared folder.
 */
function siblingObjectUri(objectUri: string, key: string): string {
  const [bucket, identity] = objectUri.replace(/^s3:\/\//, '').split('/')
  const encodedKey = key.split('/').map(encodeURIComponent).join('/')
  return `s3://${bucket}/${identity}/${encodedKey}`
}

/**
 * Presigned URLs for the images among `refs` that the document at `objectUri` may show, keyed by
 * the ref as the viewer sent it. Refs that are not images in the document's space, or that cannot
 * be signed, are left out.
 *
 * @param share the shared folder the document was opened from, if any (the config's `share`)
 */
export async function presignDocumentImages(
  objectUri: string,
  refs: readonly string[],
  share?: ShareRoot
): Promise<Record<string, string>> {
  const {key: documentKey} = splitObjectUri(objectUri)
  const wanted = Array.from(new Set(refs)).slice(0, MAX_DOCUMENT_RESOURCES)
  const signed = await Promise.allSettled(
    wanted.map(async (ref): Promise<[string, string] | undefined> => {
      const key = resolveDocumentImage(ref, documentKey)
      if (!key) return undefined
      const uri = siblingObjectUri(objectUri, key)
      const {value} = share ? await presignSharedKey(uri, share) : await presignOwnKey(uri)
      return value ? [ref, value] : undefined
    })
  )

  const urls: Record<string, string> = {}
  for (const result of signed) {
    if (result.status === 'fulfilled' && result.value) {
      urls[result.value[0]] = result.value[1]
    } else if (result.status === 'rejected') {
      getLogger().warn('Could not sign an image the document references:', result.reason)
    }
  }
  return urls
}

/** The refs of a request, or undefined when the message is not a well-formed one. */
function requestedRefs(data: unknown): {requestId: string; refs: string[]} | undefined {
  const {requestId, refs} = (data ?? {}) as {requestId?: unknown; refs?: unknown}
  if (typeof requestId !== 'string' || !Array.isArray(refs)) return undefined
  return {requestId, refs: refs.filter((ref): ref is string => typeof ref === 'string')}
}

/**
 * Answer a viewer's `resolveDocumentResources` request. Every viewer manager routes the action
 * here with the config of the iframe that asked; a viewer that is not storage-backed, or a request
 * from an unknown frame, gets an empty answer rather than none.
 */
export async function answerDocumentResources(
  e: MessageEvent,
  config: ExternalViewerConfig | undefined
): Promise<void> {
  const request = requestedRefs(e.data)
  if (!e.source || !request) return

  let urls: Record<string, string> = {}
  const objectUri = config?.objectInfo.objectUri
  if (config?.controllerInfo.dataSource === ViewerDataSource.STORAGE && objectUri) {
    try {
      urls = await presignDocumentImages(objectUri, request.refs, config.share)
    } catch (error) {
      getLogger().warn('Could not sign the images the document references:', error)
    }
  }
  ;(e.source as Window).postMessage(
    {action: RESOLVE_DOCUMENT_RESOURCES, requestId: request.requestId, urls},
    '*'
  )
}
